import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { TFunction } from 'i18next';
import type { Option } from 'components/Forms/types';
import { Icon } from 'components/Icon';
import { ConnectedFormInputProps } from 'types/form-field';
import { getTranslatedSelectOptionDescription, getTranslatedSelectOptionLabel } from 'domains/language/options';

type Props = {
  id: string;
  option: Option;
  hasError?: boolean;
  error?: string;
  tPrefixKey?: string;
  tPrefixDescriptionKey?: string;
  tFunc?: TFunction;
  className?: string;
} & Omit<ConnectedFormInputProps<HTMLInputElement>, 'label' | 'clearValue' | 'setError' | 'setValue'>;

export const CheckboxInputV2 = React.forwardRef(function CheckboxInputWithRef(
  {
    id,
    name,
    option,
    hasError,
    error,
    disabled = false,
    className,
    tFunc,
    tPrefixKey,
    tPrefixDescriptionKey,
    ...inputProps
  }: Props,
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const { value, description } = option;

  return (
    <label htmlFor={id} className="group relative flex items-center w-full">
      <div
        className={twMerge(
          clsx(
            'relative',
            'size-e40 p-e10',
            'mobile-d:size-e32 mobile-d:p-e8',
            'shrink-0',
            'rounded-full',
            {
              'hover:bg-purple-200 hover:bg-opacity-25': !disabled,
            },
            className,
          ),
        )}
      >
        <input
          {...inputProps}
          ref={ref}
          id={id}
          type="checkbox"
          name={name}
          className="peer hidden"
          value={value}
          disabled={disabled}
        />

        <div
          className={twMerge(
            clsx(
              'absolute',
              'size-e20 mobile-d:size-e16',
              'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              { 'cursor-pointer': !disabled },
              'rounded-2.5',
              'border border-warm-gray-700',
              {
                'bg-purple-300 border-0': inputProps.checked && !disabled && !hasError,
                'bg-warm-gray-400 border-0': inputProps.checked && disabled,
                'bg-utility-error border-0': inputProps.checked && hasError,
                'bg-white': !inputProps.checked,
                'border-warm-gray-400': disabled,
                'border-utility-error': hasError,
              },
            ),
          )}
        />

        {inputProps.checked && (
          <Icon
            name="Check"
            className={clsx(
              'absolute',
              'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              'size-e16 mobile-d:size-e12',
              'text-white',
              'pointer-events-none',
            )}
          />
        )}
      </div>

      <div className="ml-e12 flex flex-col">
        <span className="font-inter text-[14px] leading-[20px] text-warm-gray-900 cursor-pointer">
          {getTranslatedSelectOptionLabel(option, tFunc, tPrefixKey)}
        </span>

        {(description || tPrefixDescriptionKey) && (
          <span className="mt-1 text-xs text-warm-gray-600 cursor-default">
            {getTranslatedSelectOptionDescription(option, tFunc, tPrefixDescriptionKey)}
          </span>
        )}

        {error && <span className="mt-1 text-xs font-semibold text-utility-error cursor-default">{error}</span>}
      </div>
    </label>
  );
});
